/**
 * The single source of truth for voice PROVIDERS and the voices each one offers.
 *
 * Ported from wixel-fs's `voice-catalog.ts` — pure data, no DOM, no Node — so it
 * can be imported by the browser session, by the token-mint route, and by tests
 * alike. Every consumer that needs to turn a stored preference into a voice the
 * provider will actually accept goes through `resolveVoice`.
 *
 * Provider + voice are ONE compound setting: a voice id is only meaningful under
 * its own provider, so switching provider re-derives the voice rather than
 * carrying a now-meaningless id across.
 */

export type VoiceProvider = 'gemini' | 'grok';

/** How the voice presents. Matches `SpokenGender` in voice-system-prompt.ts. */
export type VoiceGender = 'feminine' | 'masculine';

/**
 * Which languages a voice can actually speak.
 *
 * Symphony ships 37 locales, so this is not bookkeeping: an `english-only` voice handed to a Hebrew
 * or Japanese user produces an agent that answers in the wrong language, and nothing downstream can
 * detect that — the mint succeeds, the call connects, and the voice simply speaks English at
 * someone who did not ask for it.
 *
 * Every voice we currently offer is multilingual, so today this field forbids nothing. It exists to
 * force the question at the moment a voice is ADDED, which is the only moment anyone knows the
 * answer, and to give `defaultVoiceFor` an invariant a test can hold it to.
 */
export type VoiceLanguageScope = 'multilingual' | 'english-only';

export interface VoiceOption {
  readonly id: string;
  readonly provider: VoiceProvider;
  readonly label: string;
  readonly gender: VoiceGender;
  readonly languages: VoiceLanguageScope;
}

export const VOICE_PROVIDERS: readonly VoiceProvider[] = ['gemini', 'grok'];

/**
 * Gemini ids are the Google AI Studio prebuilt voices for
 * `gemini-3.1-flash-live-preview`; Grok ids are xAI's voices for
 * `grok-voice-think-fast-1.0`. The label is the vendor's own name for the voice
 * and is deliberately NOT localized — it is a proper noun, the same in every
 * language, exactly as the model registry treats "Claude Sonnet".
 *
 * `as const satisfies` rather than a `readonly VoiceOption[]` annotation: the annotation widens
 * every `id` to `string`, and then a misspelled voice at a call site is not a compile error — it
 * travels all the way to the provider and comes back as a failed mint. Keeping the literals narrow
 * is what makes {@link VoiceId} a real union.
 *
 * `languages` is multilingual throughout: Gemini's prebuilt voices carry the model's own language
 * coverage, and xAI documents the same for its voice set (the claim wixel-fs's catalog records
 * against the same ids). Anything added later must state its own answer rather than inherit this one.
 */
export const VOICE_CATALOG = [
  { id: 'Gacrux', provider: 'gemini', label: 'Gacrux', gender: 'feminine', languages: 'multilingual' },
  { id: 'Aoede', provider: 'gemini', label: 'Aoede', gender: 'feminine', languages: 'multilingual' },
  { id: 'Kore', provider: 'gemini', label: 'Kore', gender: 'feminine', languages: 'multilingual' },
  { id: 'Charon', provider: 'gemini', label: 'Charon', gender: 'masculine', languages: 'multilingual' },
  { id: 'Puck', provider: 'gemini', label: 'Puck', gender: 'masculine', languages: 'multilingual' },
  { id: 'Fenrir', provider: 'gemini', label: 'Fenrir', gender: 'masculine', languages: 'multilingual' },
  { id: 'Ara', provider: 'grok', label: 'Ara', gender: 'feminine', languages: 'multilingual' },
  { id: 'Eve', provider: 'grok', label: 'Eve', gender: 'feminine', languages: 'multilingual' },
  { id: 'Leo', provider: 'grok', label: 'Leo', gender: 'masculine', languages: 'multilingual' },
  { id: 'Rex', provider: 'grok', label: 'Rex', gender: 'masculine', languages: 'multilingual' },
  { id: 'Sal', provider: 'grok', label: 'Sal', gender: 'masculine', languages: 'multilingual' },
] as const satisfies readonly VoiceOption[];

/** Every id the catalog offers, as a union — so a typo fails to compile instead of failing a mint. */
export type VoiceId = (typeof VOICE_CATALOG)[number]['id'];

/**
 * The voice each provider falls back to per gender. Gemini's pair is what
 * shipped before any of this existed (`VOICE_BY_GENDER`), so an account that
 * never touches the picker keeps the exact voice it has today.
 */
const DEFAULT_BY_PROVIDER_GENDER: Record<VoiceProvider, Record<VoiceGender, string>> = {
  gemini: { feminine: 'Gacrux', masculine: 'Charon' },
  grok: { feminine: 'Ara', masculine: 'Sal' },
};

export const DEFAULT_VOICE_PROVIDER: VoiceProvider = 'gemini';

/** The realtime model each provider's calls run on. */
export const VOICE_MODEL_BY_PROVIDER: Record<VoiceProvider, string> = {
  gemini: 'gemini-3.1-flash-live-preview',
  grok: 'grok-voice-think-fast-1.0',
};

/**
 * A provider is offerable only where the server holds the credential it mints with. Reporting this
 * honestly is what stops the picker offering an option that would fail at connect time with
 * nothing on screen to explain it.
 *
 * Takes the resolved secrets bag off `req.context.secrets` — it used to take `process.env`, back when
 * the credentials lived there. The parameter is structural rather than `BootstrapContext['secrets']` so
 * this module keeps its import-free graph.
 */
export function configuredProviders(
  secrets: { geminiApiKey?: string; xaiApiKey?: string } | undefined,
): Record<VoiceProvider, boolean> {
  return { gemini: Boolean(secrets?.geminiApiKey), grok: Boolean(secrets?.xaiApiKey) };
}

/**
 * Where a voice's PRE-GENERATED sample lives, or null when it hasn't been generated yet.
 *
 * Samples are static files under `public/voice-samples/`, produced offline by
 * `scripts/generate-voice-samples.ts` and committed. They are deliberately NOT synthesized at
 * render time: a picker that calls a TTS API on every ▶ press bills per curious click, adds a
 * latency the user reads as breakage, and makes the Settings screen depend on a realtime service
 * being up. `VOICES_WITH_SAMPLES` is the manifest — a voice absent from it offers no ▶ at all,
 * which is honest, rather than a button that plays silence.
 *
 * TO REGENERATE (new voice, new line, new provider, or a vendor voice update) run that script with
 * the vendor keys and paste the manifest it prints back here — its header carries the full runbook,
 * including how to add a language or a provider. Editing this list by hand without generating the
 * matching audio is caught by `test/voice-samples-manifest.test.ts`, which holds both directions:
 * a listed voice with no file renders a ▶ that plays nothing, and a committed file nobody lists is
 * dead weight shipped to every visitor.
 */
export const VOICES_WITH_SAMPLES: readonly string[] = ['Aoede', 'Ara', 'Charon', 'Eve', 'Fenrir', 'Gacrux', 'Kore', 'Leo', 'Puck', 'Rex', 'Sal'];

export function sampleUrlFor(voiceId: string, manifest: readonly string[] = VOICES_WITH_SAMPLES): string | null {
  return manifest.includes(voiceId) ? `/voice-samples/${voiceId}.mp3` : null;
}

export function voicesForProvider(provider: VoiceProvider): VoiceOption[] {
  return VOICE_CATALOG.filter((v) => v.provider === provider);
}

export function voiceById(id: string | null | undefined): VoiceOption | undefined {
  return id ? VOICE_CATALOG.find((v) => v.id === id) : undefined;
}

/** The gender-matched default for a provider — what "Default (matches your agent)" resolves to. */
export function defaultVoiceFor(provider: VoiceProvider, gender: VoiceGender): string {
  return DEFAULT_BY_PROVIDER_GENDER[provider][gender];
}

/**
 * A stored provider preference, made safe. Anything unrecognised — a provider
 * that was removed, a typo, a value from a newer client — degrades to the
 * default rather than being passed through to a mint that would fail.
 */
export function normalizeProvider(value: string | null | undefined): VoiceProvider {
  return value === 'grok' || value === 'gemini' ? value : DEFAULT_VOICE_PROVIDER;
}

/**
 * Turn a stored (provider, voice) preference into a voice the provider will
 * accept. THE rule this file exists for: a stale or unknown voice — including a
 * perfectly valid voice belonging to the OTHER provider — degrades to the
 * gender-matched default. It never propagates and never fails a mint.
 */
export function resolveVoice(
  provider: VoiceProvider,
  voice: string | null | undefined,
  gender: VoiceGender,
): string {
  const option = voiceById(voice);
  return option && option.provider === provider ? option.id : defaultVoiceFor(provider, gender);
}

/**
 * The whole mint decision in one pure place: given what the user stored and what is actually
 * possible right now, which provider and which voice does this call open with.
 *
 * Three independent things can veto the stored provider, and every one of them DEGRADES rather
 * than fails:
 *   - `clientSupports` — the caller must have a transport for that wire. A shipped native binary
 *     that only speaks Gemini must never be handed a Grok secret it would silently drop.
 *   - `available` — this deployment must hold the key. Otherwise the mint itself would 500.
 *   - the voice must belong to whatever provider survives the first two.
 *
 * `explicitGenderSwitch` is the in-call `set_voice_gender` path: the user just asked to sound
 * different, so the stored voice (which carries the old gender) must not win.
 */
export function resolveMintTarget(input: {
  storedProvider?: string | null;
  storedVoice?: string | null;
  gender: VoiceGender;
  available: Record<VoiceProvider, boolean>;
  clientSupports: readonly string[];
  explicitGenderSwitch?: boolean;
}): { provider: VoiceProvider; voiceName: string } {
  const wanted = normalizeProvider(input.storedProvider);
  const provider: VoiceProvider =
    wanted !== DEFAULT_VOICE_PROVIDER && input.available[wanted] && input.clientSupports.includes(wanted)
      ? wanted
      : DEFAULT_VOICE_PROVIDER;
  const voice = input.explicitGenderSwitch ? null : input.storedVoice;
  return { provider, voiceName: resolveVoice(provider, voice, input.gender) };
}

/**
 * Picking a provider re-derives the voice, because a voice id means nothing
 * outside its own provider. The current voice is kept only when it already
 * belongs to the newly-picked provider (the no-op case).
 */
export function voiceAfterProviderSwitch(
  provider: VoiceProvider,
  currentVoice: string | null | undefined,
  gender: VoiceGender,
): string {
  const option = voiceById(currentVoice);
  return option && option.provider === provider ? option.id : defaultVoiceFor(provider, gender);
}
